/*
----------------------------------------------------------------------------
Usage:
----------------------------------------------------------------------------
yarn add axios
import FormPost from './modules/form-post'
_('form-post').nodes().map((el) => new FormPost(el))

----------------------------------------------------------------------------
Attributes:
----------------------------------------------------------------------------
data-behaviour="form-post"          - for the form element
data-endpoint="/api/url"            - where to post to
data-element="response-container"    - where to show the response messages

*/
import { _, debug, makeGtmCall } from '../utils.js'
import EntranceTransition from './../modules/entrance-transitions.js'
import LazyLoad from './../modules/lazy-load.js'
import axios from 'axios'
import loading from '../templates/loading.js'
import Validate from '../modules/validate.js'
import successMessageContainer from '../templates/success.js'
import errorMessageContainer from '../templates/error.js'
import Bugsnag from '@bugsnag/js'
export default class FormPost {
    constructor(el) {
        this.form = el
        this.message = {
            success:
                'Thank you. Please verify your email by clicking the link emailed to you.',
            error: 'Something has gone quite wrong, please try to refresh your browser or contact us.',
        }
        this.pristine = new Validate(this.form)
        this.responseContainer = this.form.querySelector(
            _('response-container', 'element').str(),
        )

        if (this.responseContainer === null) {
            this.responseContainer = document.createElement('div')
            this.responseContainer.dataset.element = 'response-container'
            this.form.append(this.responseContainer)
        }

        this.form.addEventListener('submit', this.onSubmit)
    }

    showLoading = () => {
        this.responseContainer.innerHTML = loading()
    }

    onSubmit = (e) => {
        e.preventDefault()
        this.showLoading()
        const valid = this.pristine.validate()
        if (!valid) {
            this.handleError(
                false,
                "Please check if you've entered everything correctly",
            )
            return false
        }

        axios({
            method: this.form.dataset.action || 'post',
            url: this.form.dataset.endpoint,
            data: new FormData(this.form),
        })
            .then((response) => {
                if (response.status !== 200) {
                    return this.handleError(response)
                }
                this.handleSuccess(response)
            })
            .catch((err) => {
                if (err.response && err.response.data) {
                    this.handleError(err.response.data)
                } else {
                    this.handleError(err)
                }
            })
    }

    handleSuccess = (response, defaultMessage = this.message.success) => {
        if (response.data.result && response.data.result.message) {
            this.responseContainer.innerHTML = successMessageContainer(
                response.data.result.message,
            )
        } else if (
            response.data.data.result &&
            response.data.data.result.message
        ) {
            this.responseContainer.innerHTML = successMessageContainer(
                response.data.data.result.message,
            )
        } else {
            this.responseContainer.innerHTML =
                successMessageContainer(defaultMessage)
        }

        this.closeModal()
        this.form.dataset.gtmEvent = this.form.dataset.gtmEvent || 'form-submit'
        this.form.dataset.gtmName =
            this.form.dataset.gtmName || this.form.dataset.endpoint
        makeGtmCall(this.form)
        this.reEnableModules()
    }

    closeModal = () => {
        const url = new URL(window.location)
        const params = new URLSearchParams(url.search)
        params.delete('modal')
        window.history.pushState(
            null,
            null,
            `${window.location.pathname}${params}${window.location.hash}`,
        )
    }

    reEnableModules = () => {
        _('gtm-click')
            .nodesFrom(this.responseContainer)
            .map((el) => {
                el.addEventListener('click', () => {
                    makeGtmCall(el)
                })
            })
        _('', 'entrance')
            .nodesFrom(this.responseContainer)
            .map((el) => new EntranceTransition(el))

        _('image', 'lazyload')
            .nodesFrom(this.responseContainer)
            .map((el) => new LazyLoad(el))
    }

    handleError = (err, defaultMessage = this.message.error) => {
        if (debug) {
            console.warn({ err })
        }

        let errorHTML = `<p>${defaultMessage}</p>`

        if (err && err.errors) {
            errorHTML = `<p>${err.errors}</p>`
        }

        if (
            this.responseContainer.dataset.customError &&
            !(err && err.errors)
        ) {
            errorHTML = `<p>${this.responseContainer.dataset.customError}</p>`
        }

        this.responseContainer.innerHTML = errorMessageContainer(errorHTML)
        this.reEnableModules()
        Bugsnag.notify(new Error(errorHTML))
    }
}
